import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useStoreData } from 'grb-webui/store';
import ValidatingTextField from 'grb-webui/components/ValidatingTextField';
import {
  LICENSES_ENTITY,
} from '../../Entities';
import './LicenseSelect.scss';

const UNDEFINED_VALUE = 'UNDEFINED';

//
// LicenseSelect
//
const DefaultLicenseItem = ({ license, className }) => {
  if ((license === UNDEFINED_VALUE) || !license) {
    return (
      <div className={classnames(className, `${className}-all`)}>All licenses</div>
    );
  }
  return (
    <div className={className}>
      {license.id}
    </div>
  );
};

DefaultLicenseItem.propTypes = {
  license: PropTypes.shape({ id: PropTypes.string }),
  className: PropTypes.string,
};

DefaultLicenseItem.defaultProps = {
  license: undefined,
  className: undefined,
};

export default ({ admin, loadLicenses, variant, LicenseItem = DefaultLicenseItem, ...options }) => {
  const LicenseSelect = (props) => {
    const {
      licenses = props.allLicenses,
      value,
      onValueChange,
      size,
      allowAllLicenses,
      className,
      disabled,
    } = props;
    // const { licenseService, history, location } = props;
    const handleChange = (event, newValue) => {
      onValueChange(newValue === UNDEFINED_VALUE ? undefined : newValue.id);
      // history.push(licenseService.getStatementRoute(license.id), { referrer: location });
    };
    let licenseArray = licenses || [];
    const licenseById = {};
    licenseArray.forEach((lic) => { licenseById[lic.id] = lic; });
    if (allowAllLicenses) {
      licenseArray = [UNDEFINED_VALUE].concat(licenseArray);
    }

    const getOptionLabel = (option) => {
      return option === UNDEFINED_VALUE ? 'All licenses' : option.id;
    };

    return (
      <Autocomplete
        className={classnames('grb-license-select', className, options.className)}
        size={size}
        options={licenseArray}
        autoHighlight
        disableClearable
        getOptionLabel={getOptionLabel}
        value={value === UNDEFINED_VALUE ? UNDEFINED_VALUE : licenseById[value]}
        onChange={handleChange}
        renderOption={(option) => {
          if (!option || (option === UNDEFINED_VALUE)) {
            return <LicenseItem className="grb-license-autocomplete-item">All licenses</LicenseItem>;
          }
          return <LicenseItem license={option} className="grb-license-autocomplete-item" />;
        }}
        renderTags={(lics, getTagProps) => (
          lics.map((lic, index) => {
            const tagProps = { ...getTagProps({ index }) };
            delete tagProps.onDelete;
            if (!lic || (lic === UNDEFINED_VALUE)) {
              return <LicenseItem className="grb-license-autocomplete-item">All licenses</LicenseItem>;
            }
            return <LicenseItem license={lic} className="grb-license-autocomplete-item" />;
          })
        )}
        renderInput={params => (
          <TextField
            {...params}
            label={options.noLabelOnSelect ? undefined : 'License'}
            variant={props.inputVariant || variant}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'new-password', // disable autocomplete and autofill
            }}
          />
        )}
        style={props.style}
        disabled={disabled}
      />
    );
  };

  LicenseSelect.propTypes = {
    licenses: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
    })),
    allLicenses: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
    })),
    value: PropTypes.string,
    onValueChange: PropTypes.func,
    allowAllLicenses: PropTypes.bool,
    size: PropTypes.string,
    inputVariant: PropTypes.string,
    style: PropTypes.shape({}),
    className: PropTypes.string,
    disabled: PropTypes.bool,
  };

  LicenseSelect.defaultProps = {
    licenses: undefined,
    allLicenses: undefined,
    value: UNDEFINED_VALUE,
    onValueChange: undefined,
    allowAllLicenses: (options.allowAllLicenses === undefined) || options.allowAllLicenses,
    size: 'small',
    inputVariant: undefined,
    style: undefined,
    className: undefined,
    disabled: undefined,
  };

  //
  // LicenseTextField
  //
  const LicenseTextField = (props) => {
    const { value, onValueChange, licenseService, style, autoFocus, className } = props;

    const validateLicenseId = useCallback((id) => {
      return licenseService.getLicense(id).catch(() => { throw new Error('Unkown license id'); });
    }, [licenseService]);

    return (
      <ValidatingTextField
        className={classnames('grb-license-textfield', className)}
        label="License"
        value={value}
        onChange={onValueChange}
        validateValue={validateLicenseId}
        variant={variant}
        style={style}
        autoFocus={autoFocus}
      />
    );
  };

  LicenseTextField.propTypes = {
    value: PropTypes.string,
    onValueChange: PropTypes.func,
    style: PropTypes.shape({}),
    licenseService: PropTypes.shape({
      getLicense: PropTypes.func,
    }),
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
  };

  LicenseTextField.defaultProps = {
    value: undefined,
    onValueChange: undefined,
    style: undefined,
    licenseService: undefined,
    autoFocus: undefined,
    className: undefined,
  };

  const load = loadLicenses === undefined ? !admin : loadLicenses;
  if (load) {
    return (props) => {
      const pps = props;
      const { licenses } = useStoreData(pps.store, [LICENSES_ENTITY]);
      return <LicenseSelect {...props} licenses={licenses || []} disabled={!licenses} />;
    };
  }
  if (variant) {
    const Component = admin ? LicenseTextField : LicenseSelect;
    return (props) => <Component {...props} variant={variant} />;
  }
  return admin ? LicenseTextField : LicenseSelect;
};
